/**
 * 取引価格（成約事例）APIのレスポンス
 */
export interface TradePriceData {
  /** 価格カテゴリー */
  priceCategory: string;
  /** 種類（宅地、中古マンション等） */
  type: string;
  /** 地域 */
  region: string;
  /** 市区町村コード */
  municipalityCode: string;
  /** 都道府県 */
  prefecture: string;
  /** 市区町村 */
  municipality: string;
  /** 地区名 */
  districtName: string;
  /** 取引価格 */
  tradePrice: string;
  /** 坪単価 */
  pricePerUnit: string;
  /** 間取り */
  floorPlan: string;
  /** 面積 */
  area: string;
  /** ㎡単価 */
  unitPrice: string;
  /** 土地の形状 */
  landShape: string;
  /** 間口 */
  frontage: string;
  /** 延床面積 */
  totalFloorArea: string;
  /** 建築年 */
  buildingYear: string;
  /** 構造 */
  structure: string;
  /** 用途 */
  use: string;
  /** 今後の利用目的 */
  purpose: string;
  /** 方位 */
  direction: string;
  /** 分類 */
  classification: string;
  /** 奥行 */
  breadth: string;
  /** 都市計画 */
  cityPlanning: string;
  /** 建ぺい率 */
  coverageRatio: string;
  /** 容積率 */
  floorAreaRatio: string;
  /** 取引時期 */
  period: string;
  /** 改装 */
  renovation: string;
  /** 備考 */
  remarks: string;
  /** 地区コード */
  districtCode: string;
}

/**
 * 価格情報区分種別
 */
export type PriceClassificationType = "realEstateTransaction" | "contractPrice";

/**
 * 物件種別
 */
export type PropertyType = "land" | "landWithBuilding" | "usedCondominium";

/**
 * 取引価格タイルレイヤーのフィルタ設定型
 */
export interface TradePriceTileFilter {
  /** 価格情報区分（複数選択可能） */
  priceClassifications: PriceClassificationType[];
  /** 取引時期（何年前まで） */
  periodYears: 1 | 2 | 5;
  /** 物件種別（複数選択可能） */
  propertyTypes: PropertyType[];
}

/**
 * デフォルトの取引価格タイルフィルタ設定
 */
export const DEFAULT_TRADE_PRICE_TILE_FILTER: TradePriceTileFilter = {
  priceClassifications: ["realEstateTransaction", "contractPrice"],
  periodYears: 1,
  propertyTypes: ["land", "landWithBuilding", "usedCondominium"],
};

/**
 * 取引価格一覧検索のフィルタ設定型
 */
export interface TradePriceFilter {
  /** 価格情報区分（複数選択可能） */
  priceClassifications: PriceClassificationType[];
  /** 取引時期（年） */
  year: number;
}

/**
 * 取引価格一覧のフィルタ設定型（都道府県を含む）
 */
export interface TradePriceListFilter extends TradePriceFilter {
  /** 都道府県コード */
  prefecture: number | null;
  /** 住所（都道府県を除く） */
  location?: string;
}

/**
 * デフォルトのフィルタ設定を取得する関数
 * @returns デフォルトのフィルタ設定（取引時期は前年）
 */
export const getDefaultTradePriceFilter = (): TradePriceFilter => {
  const currentYear = new Date().getFullYear();
  return {
    priceClassifications: ["realEstateTransaction", "contractPrice"],
    year: currentYear - 1,
  };
};

// 価格情報区分
export type PriceClassificationCode = "01" | "02";

// 物件種別
export type PropertyTypeCode = "01" | "02" | "07";

// 検索フォームのデータ型
export interface TradePriceSearchFormData {
  prefecture: number[];
  city: number[];
  address: string;
  priceClassifications: PriceClassificationCode[];
  year: number;
  quarter?: number;
}

// APIリクエストパラメータ型
export interface TradePriceSearchAPIParams {
  prefecture: number;
  priceClassifications?: string;
  year?: number;
  location?: string;
}

// APIレスポンスのデータ型（キャメルケースで返却される）
export interface TradePriceDataItem {
  priceClassification: string;
  type: string;
  region: string;
  municipalityCode: string;
  prefecture: string;
  municipality: string;
  districtName: string;
  tradePrice: string;
  pricePerUnit: string;
  floorPlan: string;
  area: string;
  unitPrice: string;
  landShape: string;
  frontage: string;
  totalFloorArea: string;
  buildingYear: string;
  structure: string;
  use: string;
  purpose: string;
  direction: string;
  classification: string;
  breadth: string;
  cityPlanning: string;
  coverageRatio: string;
  floorAreaRatio: string;
  period: string;
  renovation: string;
  remarks: string;
  priceCategory: string;
  districtCode: string;
}

// グリッド表示用のデータ型（チェックボックス用のフラグを追加）
export interface TradePriceGridRow extends TradePriceDataItem {
  id: string; // ユニークID（インデックスベース）
  selected?: boolean; // チェックボックスの選択状態
}
